import { useEffect } from "react";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import GreenLeverCalculator from "@/components/GreenLeverCalculator";
import { Link } from "react-router-dom";
import { applySeo } from "@/lib/seo";

export default function CalculatorGreenLever() {
  useEffect(() => {
    applySeo({
      title: "Grüne Hebel-Simulator – Impact-Rechner | Grüne Geldanlage",
      description: "Der Impact-Rechner übersetzt die jährliche CO₂-Ersparnis Ihrer nachhaltigen Anlage in greifbare Vergleiche: vegane Ernährung, Autokilometer, gepflanzte Bäume.",
      canonicalPath: "/rechner/gruener-hebel",
    });
  }, []);

  return (
    <div className="min-h-screen bg-brand-paper" data-testid="calc-green-lever-page">
      <Header />
      <section className="max-w-4xl mx-auto px-6 lg:px-10 pt-20 pb-10">
        <div className="overline mb-4">Rechner</div>
        <h1 className="font-serif text-5xl md:text-6xl font-light leading-tight tracking-tight text-brand-dark mb-6">
          Grüne Hebel-Simulator
        </h1>
        <p className="text-lg text-neutral-700 max-w-3xl mb-10">
          Ihr Depot bewegt mehr, als Sie denken. Der Impact-Rechner setzt die jährliche
          CO₂-Ersparnis nachhaltiger Geldanlagen ins Verhältnis zu drei greifbaren
          Alltagsentscheidungen – Ernährung, Mobilität, Aufforstung. Ergebnis: ein
          Aha-Effekt, der die Wirkung Ihres Kapitals konkret sichtbar macht.
        </p>
      </section>

      <section className="max-w-6xl mx-auto px-6 lg:px-10 pb-20" data-testid="calc-green-lever-tool">
        <GreenLeverCalculator />
      </section>

      <section className="max-w-4xl mx-auto px-6 lg:px-10 pb-24">
        <div className="prose-editorial max-w-none">
          <h2>Warum dieser Rechner? Der unterschätzte Hebel Ihres Geldes</h2>
          <p>
            Die meisten Menschen unterschätzen systematisch, welchen Klima-Einfluss ihr
            angelegtes Kapital hat – und überschätzen umgekehrt, wie viel eine einzelne
            Konsumentscheidung im Alltag bewegt. Eine kalt geduschte Woche wirkt sich in
            CO₂-Bilanzen weit weniger aus als 10.000 Euro, die in ein Impact-Portfolio
            umgeschichtet werden. Der Grüne Hebel-Simulator macht diesen Unterschied sichtbar:
            Er übersetzt die Größenordnung, in der Ihr Depot wirkt, in die Sprache
            alltäglicher Vergleiche. Nicht als moralische Aufforderung, sondern als
            faktenbasierter Realitätsabgleich.
          </p>

          <h2>Wie funktioniert die Berechnung?</h2>
          <p>
            Die Logik ist bewusst einfach gehalten. Grundlage ist eine empirisch validierte
            Referenzgröße: Wer 10.000 Euro von einer konventionellen in eine nachhaltige
            Anlage überträgt, senkt die jährlich mitfinanzierten CO₂-Emissionen um rund
            1,5 Tonnen. Daraus ergibt sich ein linearer Koeffizient von etwa 0,15 Kilogramm
            CO₂-Ersparnis pro angelegtem Euro und Jahr. Diesen Wert übersetzt der Rechner in
            drei greifbare Vergleichsgrößen: die Monate rein pflanzlicher Ernährung, die
            Kilometer nicht gefahrener Autofahrten und die Zahl an neu gepflanzten Bäumen, die
            für die gleiche Einsparung nötig wären. So entsteht ein Perspektivwechsel: Aus
            einer abstrakten Tonnenangabe wird eine Reihe von Alltagsentscheidungen, die man
            emotional greifen kann.
          </p>

          <h2>Die Referenzwerte im Detail</h2>
          <p>
            Für den <strong>Ernährungsvergleich</strong> setzen wir 1.500 Kilogramm
            CO₂-Ersparnis pro Jahr bei komplett veganer Kost gegenüber einer typischen
            deutschen Mischkost an – ein Wert, der sich in Meta-Studien der letzten Jahre
            konsistent zeigt und Emissionen entlang der gesamten Lieferkette einschließt.
            Für den <strong>Automobil-Vergleich</strong> arbeiten wir mit 176 Gramm CO₂ pro
            Kilometer – dem durchschnittlichen Realverbrauch eines Personenwagens in
            Deutschland, inklusive Kraftstoffvorkette. Der <strong>Baum-Vergleich</strong>{" "}
            berücksichtigt eine mittelalte Buche mit einer Netto-CO₂-Aufnahme von etwa
            12,5 Kilogramm pro Jahr. Diese Zahlen sind Durchschnittswerte; die reale
            Wirkung schwankt je nach individuellem Lebensstil, Fahrzeugtyp und
            Baumart. Sie liefern jedoch belastbare Größenordnungen für einen fairen
            Vergleich.
          </p>

          <h2>Grenzen dieses Modells</h2>
          <p>
            So eindrucksvoll die Vergleichszahlen wirken – sie sind ein Modell, keine
            Klimabilanz. Der Rechner unterstellt einen linearen Zusammenhang zwischen
            angelegter Summe und jährlicher CO₂-Ersparnis. In der Realität hängt die Wirkung
            stark davon ab, in welche Art nachhaltiger Anlage investiert wird: Ein
            Paris-Aligned-ETF hat einen anderen Fußabdruck als ein dediziertes
            Impact-Portfolio; Green Bonds wirken anders als börsennotierte Cleantech-Aktien.
            Zudem gilt: Kapitalmarktinvestitionen wirken über Refinanzierungseffekte und
            Signalwirkung an den Kapitalmärkten – die reale Einsparung ist damit indirekt
            und schwerer zu attribuieren als der Verzicht auf einen Flug. Für eine tiefere
            Analyse Ihres konkreten Depot-Fußabdrucks empfehlen wir den{" "}
            <Link to="/rechner/co2-fussabdruck">CO₂-Fußabdruck-Portfolio-Rechner</Link>, der
            den Depot-Mix differenziert bewertet.
          </p>

          <h2>Der psychologische Wert des Vergleichs</h2>
          <p>
            Warum überhaupt in Alltagsäquivalente umrechnen? Weil der menschliche Verstand
            Tonnen nicht intuitiv erfasst. Zwölf Monate vegane Ernährung sind eine
            Vorstellung; 1,5 Tonnen CO₂ sind eine Zahl. Studien aus der Verhaltensökonomie
            zeigen: Klimafakten wirken deutlich stärker, wenn sie an eigene
            Handlungserfahrungen andocken. Wer schon einmal versucht hat, einen Monat
            konsequent pflanzlich zu essen, weiß, wie viel Disziplin dahintersteckt – und
            begreift damit sofort, welche Größenordnung an CO₂-Einsparung ein kleiner
            Umschichtungsschritt im Depot bewegt. Der Rechner ist damit weniger ein
            wissenschaftliches Instrument als ein didaktisches: Er macht die stille Wirkung
            nachhaltigen Investierens hörbar.
          </p>

          <h2>Was Sie mit diesem Rechner tun können</h2>
          <p>
            Nutzen Sie den Grünen Hebel-Simulator als Ausgangspunkt für drei konkrete
            Gedankenschritte. Erstens: Beziffern Sie Ihre eigene Anlagesumme in
            konventionellen Vehikeln – wie groß wäre der Impact, wenn Sie einen Teil davon
            in nachhaltige Alternativen umschichten? Zweitens: Vergleichen Sie das Ergebnis
            mit Ihrem persönlichen Klima-Engagement im Alltag. Wo liegt der größere Hebel?
            Drittens: Kombinieren Sie den Rechner mit unseren anderen Werkzeugen –
            der <Link to="/rechner/nachhaltigkeits-rendite">Nachhaltigkeits-Rendite-Rechner</Link>{" "}
            zeigt Ihnen die finanzielle Perspektive, der{" "}
            <Link to="/rechner/green-premium">Green Premium Rechner</Link>{" "}
            beziffert die Opportunitätskosten. Zusammen entsteht ein vollständiges Bild:
            Wirkung, Rendite und Kosten in einer Ansicht.
          </p>
          <p>
            Noch unsicher, welcher nachhaltige ETF überhaupt zu Ihnen passt? Unser{" "}
            <Link to="/rechner/nachhaltiger-etf-finder">ESG-Fit-Quiz</Link> filtert aus
            25 kuratierten ETFs die passende Empfehlung für Ihre Kriterien.
          </p>

          <h2>Fazit: Kapital ist der stärkste Klima-Hebel im Privathaushalt</h2>
          <p>
            Wenige Entscheidungen im Leben eines Privathaushalts entfalten so viel Wirkung wie
            die Umschichtung eines nennenswerten Kapitalbetrags in nachhaltige Anlagen –
            und gleichzeitig ist keine Entscheidung so unauffällig. Der Grüne
            Hebel-Simulator macht diesen unterschätzten Hebel sichtbar. Er ersetzt keine
            fundierte Beratung und keine detaillierte Klimabilanz, aber er liefert ein
            Argument gegen die weit verbreitete Annahme, nachhaltige Geldanlage sei ein
            zweitrangiger Nebenschauplatz. Wer die Zahl auf dem Bildschirm einmal gesehen
            hat, denkt anders über die nächste Depotanpassung nach.
          </p>
        </div>
      </section>
      <Footer />
    </div>
  );
}
